import { Injectable } from '@angular/core';
import { Session, User } from '@supabase/supabase-js';
import { AuthRepository } from './auth.repository';
import { ILoginPayload, IRegisterPayload, IUpdateMyProfilePayload, IUser, UserRole } from '../models/auth.model';
import { AuthMapper } from '../mappers/auth.mapper';
import { Result, fail, ok } from '../../shared/types/result.type';
import { AuthenticationError } from '../errors/auth.error';
import { supabaseClient } from '../../shared/supabase.client';
import { UnexpectedError, ValidationError } from '../../shared/errors/domain-error';
import { LearningTrack } from '../../shared/types/learning-track.type';
import { GET_MY_CONTEXT_RPC, GET_TEACHER_ID_BY_DOMAIN_RPC } from '../constants/auth.constants';

@Injectable({
  providedIn: 'root',
})
export class SupabaseAuthRepository implements AuthRepository {
  private readonly supabase = supabaseClient;

  async login(payload: ILoginPayload): Promise<Result<IUser>> {
    try {
      const { data, error } = await this.supabase.auth.signInWithPassword({
        email: payload.email,
        password: payload.password ?? '',
      });

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      if (!data.user) {
        return fail(new AuthenticationError('Login failed: No user returned.'));
      }

      return this.fetchUserProfile(data.user);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during login.', err));
    }
  }

  async registerStudent(payload: IRegisterPayload): Promise<Result<IUser>> {
    try {
      const { data, error } = await this.supabase.auth.signUp({
        email: payload.email,
        password: payload.password ?? '',
        options: {
          // Verification is done with an email OTP code (see `verifyRegistrationOtp`),
          // entered on the register page — no redirect is involved. This
          // `emailRedirectTo` only matters if the email template still exposes
          // the magic link as a fallback; the link lands the already-signed-in
          // student on /student/home (the trigger has created their row, so they
          // resolve as a full `student`). Harmless when the template is code-only.
          emailRedirectTo: `${window.location.origin}/student/home`,
          // The full profile the trigger needs, since the app cannot INSERT the
          // `students` row itself when email confirmation leaves signUp with no
          // session (auth.uid() is NULL). snake_case to match the trigger reads
          // and the `students` columns. See the 20260909120000 migration.
          data: {
            name: payload.name,
            phone: payload.phone,
            grade_id: payload.gradeId,
            teacher_id: payload.teacherId,
            learning_track: payload.learningTrack,
          },
        },
      });

      if (error) {
        if (error.message.toLowerCase().includes('already exists')) {
          return fail(new ValidationError(error.message, error));
        }
        return fail(new AuthenticationError(error.message, error));
      }

      if (!data.user) {
        return fail(new AuthenticationError('Registration failed: No user returned.'));
      }

      // The `students` row is now created by the `handle_new_student_user`
      // trigger from the sign-up metadata above (the app can't INSERT it when
      // email confirmation leaves signUp with no session — auth.uid() is NULL).
      // See the 20260909120000 migration.

      // Supabase's "Confirm email" setting leaves `signUp()` with a user but no
      // session. Tell the caller to prompt for the emailed OTP code; the student
      // enters it and `verifyRegistrationOtp()` completes the sign-in. The
      // `students` row already exists (trigger), so they resolve as a full
      // `student` once verified.
      if (!data.session) {
        const pendingUser = AuthMapper.toUserModel(data.user, {
          role: 'incomplete_student',
          name: payload.name,
          phone: payload.phone,
          emailConfirmationRequired: true,
        });
        return ok(pendingUser);
      }

      // Confirmation disabled: a session exists and the trigger has already
      // created the row, so resolve the full profile rather than inserting a
      // second (conflicting) row. `fetchUserProfile()` derives the role from the
      // freshly-created `students` row → `student`.
      return this.fetchUserProfile(data.user);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during registration.', err));
    }
  }

  /**
   * Confirms a fresh registration with the 6-digit code Supabase emailed
   * (`type: 'signup'`). On success Supabase returns a session — the student is
   * now signed in — and the `handle_new_student_user` trigger has already
   * created their `students` row, so `fetchUserProfile()` resolves them as a
   * complete `student`. This is the OTP counterpart of clicking the magic link.
   */
  async verifyRegistrationOtp(email: string, token: string): Promise<Result<IUser>> {
    try {
      const { data, error } = await this.supabase.auth.verifyOtp({
        email,
        token,
        type: 'signup',
      });

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      if (!data.user) {
        return fail(new AuthenticationError('Verification failed: No user returned.'));
      }

      return this.fetchUserProfile(data.user);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during verification.', err));
    }
  }

  /**
   * Re-sends the registration OTP to a student who did not receive or let expire
   * the first code. `type: 'signup'` matches the pending, unconfirmed sign-up.
   */
  async resendRegistrationOtp(email: string): Promise<Result<void>> {
    try {
      const { error } = await this.supabase.auth.resend({ type: 'signup', email });

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      return ok(undefined);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while resending the code.', err));
    }
  }

  async logout(): Promise<Result<void>> {
    try {
      const { error } = await this.supabase.auth.signOut();
      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }
      return ok(undefined);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during logout.', err));
    }
  }

  async getCurrentSession(): Promise<Result<IUser | null>> {
    try {
      const { data: { session }, error } = await this.supabase.auth.getSession();
      
      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      if (!session || !session.user) {
        return ok(null);
      }

      return this.fetchUserProfile(session.user);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while fetching session.', err));
    }
  }

  async loginWithGoogle(): Promise<Result<void>> {
    try {
      const { error } = await this.supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      return ok(undefined);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during Google login.', err));
    }
  }

  async resetPasswordForEmail(email: string): Promise<Result<void>> {
    try {
      const { error } = await this.supabase.auth.resetPasswordForEmail(email);

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      return ok(undefined);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred during password reset.', err));
    }
  }

  /**
   * Re-verifies `currentPassword` via a throwaway `signInWithPassword` (the same
   * user/session — the id is unchanged, so the facade's auth listener stands
   * down and no profile re-fetch happens), then rotates to `newPassword`. Only
   * hits Supabase Auth; no `students`/database write is involved.
   */
  async changePassword(payload: { email: string; currentPassword: string; newPassword: string }): Promise<Result<void>> {
    try {
      const { error: verifyError } = await this.supabase.auth.signInWithPassword({
        email: payload.email,
        password: payload.currentPassword,
      });

      if (verifyError) {
        return fail(new ValidationError('Current password is incorrect.', verifyError));
      }

      const { error } = await this.supabase.auth.updateUser({ password: payload.newPassword });

      if (error) {
        return fail(new AuthenticationError(error.message, error));
      }

      return ok(undefined);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while changing the password.', err));
    }
  }

  async completeRegistration(payload: { phone: string; gradeId: string; teacherId: string; learningTrack: LearningTrack }): Promise<Result<IUser>> {
    try {
      const { data: { session }, error: sessionError } = await this.supabase.auth.getSession();
      
      if (sessionError || !session || !session.user) {
        return fail(new AuthenticationError('No active session found.', sessionError));
      }

      const user = session.user;
      const name = user.user_metadata?.['full_name'] || user.user_metadata?.['name'] || 'Unknown Name';

      const { error: studentError } = await this.supabase
        .from('students')
        .insert({
          id: user.id,
          name: name,
          phone: payload.phone,
          grade_id: payload.gradeId,
          teacher_id: payload.teacherId,
          learning_track: payload.learningTrack,
        });

      if (studentError) {
        if (studentError.message.toLowerCase().includes('already exists') || studentError.code === '23505') {
            return fail(new ValidationError('This phone number is already registered.', studentError));
        }
        return fail(new UnexpectedError('Failed to complete profile.', studentError));
      }

      return this.fetchUserProfile(user);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while completing registration.', err));
    }
  }

  /**
   * Upserts the caller's own `students` row rather than a plain UPDATE: a
   * student always has one (created at registration), but a teacher may not
   * (see `fetchUserProfile()`'s doc-comment) — the first time a teacher
   * saves their profile, this creates the row. `name`/`phone` are always
   * sent because they're `NOT NULL` with no default; the INSERT branch of
   * the upsert would violate that constraint if they were omitted, even
   * though the UPDATE branch (existing row) wouldn't need them.
   *
   * Relies on the same self-service RLS policies registration already uses
   * — "Students can insert own profile" / "Students can update own profile",
   * both `id = auth.uid()` — no new policy was needed for this.
   */
  async updateMyProfile(
    userId: string,
    payload: IUpdateMyProfilePayload,
  ): Promise<Result<{ name: string; phone: string; avatarUrl?: string }>> {
    try {
      const row: { id: string; name: string; phone: string; avatar_url?: string } = {
        id: userId,
        name: payload.name,
        phone: payload.phone,
      };
      if (payload.avatarUrl !== undefined) {
        row.avatar_url = payload.avatarUrl;
      }

      const { data, error } = await this.supabase
        .from('students')
        .upsert(row, { onConflict: 'id' })
        .select('name, phone, avatar_url')
        .single();

      if (error) {
        if (error.code === '23505') {
          return fail(new ValidationError('This phone number is already registered.', error));
        }
        return fail(new UnexpectedError('Failed to update profile.', error));
      }

      return ok({
        name: data.name,
        phone: data.phone,
        avatarUrl: data.avatar_url ?? undefined,
      });
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while updating profile.', err));
    }
  }

  /**
   * Writes the caller's own track. Plain UPDATE, not an upsert: only a student
   * with an existing row ever calls this (registration sets the column inline,
   * and the chooser is only reachable for a signed-in student), so an INSERT
   * branch here could only ever create a `students` row for a teacher — the one
   * case that must not get a track.
   *
   * `42501` is the `lock_learning_track` trigger refusing a second write, which
   * is a rule, not a fault — it surfaces as a `ValidationError` carrying the
   * "ask your teacher" message rather than an unexpected failure.
   */
  async setMyLearningTrack(userId: string, track: LearningTrack): Promise<Result<LearningTrack>> {
    try {
      const { data, error } = await this.supabase
        .from('students')
        .update({ learning_track: track, updated_at: new Date().toISOString() })
        .eq('id', userId)
        .select('learning_track')
        .single();

      if (error) {
        if (error.code === '42501') {
          return fail(new ValidationError('Your track is locked. Ask your teacher to change it.', error));
        }
        return fail(new UnexpectedError('Failed to save your learning track.', error));
      }

      return ok(data.learning_track as LearningTrack);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while saving your learning track.', err));
    }
  }

  async getTeacherIdByDomain(domain: string): Promise<Result<string | null>> {
    try {
      const { data, error } = await this.supabase.rpc(GET_TEACHER_ID_BY_DOMAIN_RPC, {
        p_domain: domain,
      });

      if (error) {
        return fail(new UnexpectedError('Failed to resolve the academy for this domain.', error));
      }

      // The RPC returns a scalar uuid, or null for an unknown/lapsed domain.
      return ok((data as string | null) ?? null);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while resolving the domain.', err));
    }
  }

  onAuthStateChange(callback: (event: string, session: Session | null) => void): { unsubscribe: () => void } {
    const { data } = this.supabase.auth.onAuthStateChange(callback);
    return {
      unsubscribe: () => data.subscription.unsubscribe(),
    };
  }

  /**
   * Resolves the application role for an authenticated user.
   *
   * Role is **derived, never stored on the auth user**. The two questions are
   * asked in parallel because neither depends on the other:
   *
   * 1. `admins` via the `is_admin()` RPC → `teacher`.
   * 2. a row in `students` → `student`.
   * 3. neither → `incomplete_student`, which the guards route to `/onboarding`.
   *
   * **Admin is checked against the RPC, not the table.** `admins` has RLS
   * enabled with no policies, so a direct select returns nothing for everyone.
   * `is_admin()` is `SECURITY DEFINER` and is the exact function the
   * "Admin full access" policies use, so the UI cannot disagree with the
   * database about who may write.
   *
   * **Admin wins over student** if a user is somehow in both tables, because
   * `is_admin()` is what actually governs writes — the shell the user sees
   * should be the one whose actions will succeed.
   *
   * **There is no default to `teacher`.** It used to be the fallback for any
   * authenticated non-Google user, which admitted strangers to the whole
   * `/teacher` shell and then let RLS reject every write — an admin UI that
   * looked broken rather than forbidden.
   *
   * A failed lookup returns a `Failure` rather than guessing a role: guessing
   * low would push a real admin into onboarding, where completing the form
   * would insert a `students` row and permanently mis-role them.
   */
  private async fetchUserProfile(user: User): Promise<Result<IUser>> {
    try {
      const [studentResult, contextResult] = await Promise.all([
        this.supabase
          .from('students')
          .select('name, phone, grade_id, avatar_url, learning_track')
          .eq('id', user.id)
          .maybeSingle(),
        this.supabase.rpc(GET_MY_CONTEXT_RPC).single(),
      ]);

      if (studentResult.error) {
        return fail(
          new UnexpectedError('Failed to fetch user profile details.', studentResult.error),
        );
      }

      if (contextResult.error) {
        return fail(
          new UnexpectedError('Failed to resolve user context.', contextResult.error),
        );
      }

      const context = contextResult.data as {
        role: UserRole;
        teacher_id: string | null;
        subscription_status: string | null;
        subscription_expires_at: string | null;
        teacher_name: string | null;
      };
      const studentData = studentResult.data;
      const metadataName =
        user.user_metadata?.['full_name'] || user.user_metadata?.['name'] || '';
      const teacherId = context.teacher_id ?? undefined;
      const teacherName = context.teacher_name ?? undefined;
      const subscriptionStatus = context.subscription_status ?? undefined;
      const subscriptionExpiresAt = context.subscription_expires_at ?? null;

      let profileData: {
        role: UserRole;
        name?: string;
        phone?: string;
        gradeId?: string;
        avatarUrl?: string;
        teacherId?: string;
        teacherName?: string;
        subscriptionStatus?: string;
        subscriptionExpiresAt?: string | null;
        learningTrack?: LearningTrack | null;
      };

      switch (context.role) {
        case 'teacher':
          // A teacher isn't required to have a `students` row (they don't go
          // through student registration), but the self-service profile page
          // may have lazily created one via updateMyProfile()'s upsert — use
          // it when present instead of always falling back to auth metadata.
          // For a teacher, teacher_id is their own id.
          profileData = {
            role: 'teacher',
            name: studentData?.name || metadataName,
            phone: studentData?.phone,
            avatarUrl: studentData?.avatar_url ?? undefined,
            teacherId,
            teacherName,
            subscriptionStatus,
            subscriptionExpiresAt,
          };
          break;

        case 'super_admin':
          // Platform admin — not a tenant, so no teacher_id/subscription. They
          // may still have a lazily-created `students` row for their name.
          profileData = {
            role: 'super_admin',
            name: studentData?.name || metadataName,
            phone: studentData?.phone,
            avatarUrl: studentData?.avatar_url ?? undefined,
          };
          break;

        case 'student':
          profileData = {
            role: 'student',
            name: studentData?.name ?? metadataName,
            phone: studentData?.phone,
            gradeId: studentData?.grade_id,
            avatarUrl: studentData?.avatar_url ?? undefined,
            teacherId,
            teacherName,
            subscriptionStatus,
            // `null` for anyone who registered before the column existed — the
            // shell routes them to the one-time chooser rather than guessing.
            learningTrack: (studentData?.learning_track as LearningTrack | null) ?? null,
          };
          break;

        default:
          // No student row yet and not admin/teacher. Covers the Google user
          // who has not onboarded, and the email/password user whose `students`
          // insert failed during registration — onboarding lets both self-heal.
          profileData = {
            role: 'incomplete_student',
            name: metadataName,
          };
      }

      const userModel = AuthMapper.toUserModel(user, profileData);
      return ok(userModel);
    } catch (err) {
      return fail(new UnexpectedError('An unexpected error occurred while fetching profile.', err));
    }
  }
}
